import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, of, tap } from 'rxjs';
import {
	SenderEventActionRequest,
	SenderEventActionResponse,
	SenderSubscription,
	SenderSubscriptionEvent,
} from '@campaign-portal/namespace/entities/sender-id/specs';
import {
	CreateResponse,
	ReadResponse,
	UpdateRequest,
	UpdateResponse,
} from '@campaign-portal/namespace/common/implementations';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import { RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { SenderSubscriptionEventStatus } from '@campaign-portal/namespace/common/enums';
import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisLanguageService,
	AlarisProfileService,
	AlarisToasterService,
	ChannelUtilsService,
	ErrorNotifierConfig,
	ExtendableRefBookService,
	PROFILE_SERVICE_INJECTOR,
	sortData,
} from '@campaign-portal/components-library';
import { CP_PERMISSIONS, SenderSubscriptionEntity } from '../types';

@Injectable({
	providedIn: 'root',
})
export class SendersService
	extends ExtendableRefBookService<SenderSubscriptionEntity<exist>>
	implements AbstractCRUDService
{
	readonly active$ = new BehaviorSubject<SenderSubscriptionEntity<exist>[]>([]);

	readonly validPeriodStatuses: (SenderSubscriptionEventStatus | undefined)[] = [
		SenderSubscriptionEventStatus.PERIOD_CHANGED,
		SenderSubscriptionEventStatus.CONFIRMED,
		SenderSubscriptionEventStatus.UNSUBSCRIBED,
	];

	constructor(
		private readonly api: AlarisApiService,
		private readonly alarisToaster: AlarisToasterService,
		private readonly langService: AlarisLanguageService,
		private readonly cu: ChannelUtilsService,
		@Inject(PROFILE_SERVICE_INJECTOR) private readonly profileService: AlarisProfileService
	) {
		super();
		this.list$.subscribe((list) => {
			this.active$.next(list.filter((sender) => sender.isActive()));
		});
	}

	get entity(): string {
		return this.langService.translate('notifications.entities.sender');
	}

	get title(): string {
		return this.langService.translate('notifications.titles.sender');
	}

	getNameWithChannel(id: Id<exist>): string {
		const sender = this.map.get(id);
		const channel = this.langService.translate(this.cu.name(sender?.trafficType));
		return (sender?.name ?? '') + ` (${channel})`;
	}

	read(params?: RPCRequestParams): Observable<ReadResponse<SenderSubscriptionEntity<exist>[]>> {
		return this.api
			.loader<ReadResponse<SenderSubscription<exist>[]>>('Senders.Read', params, this.loading$, this.errorNotifier)
			.pipe(map((resp) => ({ ...resp, Data: resp.Data.map((s) => this.toEntity(s)) })));
	}

	override load(): Observable<ReadResponse<SenderSubscriptionEntity<exist>[]>> {
		if (this.profileService.allowed([CP_PERMISSIONS.SENDERS_R])) {
			return this.api
				.loader<ReadResponse<SenderSubscription<exist>[]>>('Senders.Read', undefined, this.loading$, this.errorNotifier)
				.pipe(
					map((resp) => {
						const entities = resp.Data.map((s) => this.toEntity(s));
						sortData(entities, 'name');
						return super.process({ ...resp, Data: entities });
					})
				);
		}
		return of({ Success: true, Total: 0, Data: [] });
	}

	toEntity(sender: SenderSubscription<exist>): SenderSubscriptionEntity<exist> {
		return new SenderSubscriptionEntity(sender);
	}

	create(): Observable<void> {
		return of(undefined);
	}

	update(): Observable<void> {
		return of(undefined);
	}

	requestSenderId(
		senderIds: SenderSubscription | SenderSubscription[]
	): Observable<CreateResponse<SenderSubscription<exist>>> {
		const Entities = Array.isArray(senderIds) ? senderIds : [senderIds];
		const params: RPCRequestParams = { Data: { Entities } };
		const notify = (response: CreateResponse<SenderSubscription<exist>>): void => {
			const message = this.langService.translate('notifications.actions.request', { entity: this.entity });
			if (response.Success) {
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<
				CreateResponse<SenderSubscription<exist>>
			>('Senders.Create', params, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap(() => {
					this.refresh$.next();
				})
			);
	}

	delete(sender: SenderSubscription<exist>): Observable<UpdateResponse<SenderSubscription<exist>>> {
		const params: UpdateRequest<SenderSubscription<exist>> = { Data: { Entities: [sender] } };
		const notify = (response: UpdateResponse<SenderSubscription<exist>>): void => {
			const message = this.langService.translate('notifications.actions.delete', { entity: this.entity });
			if (response.Success) {
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<
				UpdateResponse<SenderSubscription<exist>>
			>('Senders.Delete', params, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap(() => {
					this.refresh$.next();
				})
			);
	}

	unsubscribe(event: SenderSubscriptionEvent): Observable<SenderEventActionResponse> {
		const params: SenderEventActionRequest = {
			Data: {
				Ids: [event.sender.id],
				Entities: [event],
			},
		};
		const notify = (response: SenderEventActionResponse): void => {
			const message = this.langService.translate('notifications.actions.unsubscribe', { entity: this.entity });
			if (response.Success) {
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<SenderEventActionResponse>('SenderEvents.Unsubscribe', params, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap(() => {
					this.refresh$.next();
				})
			);
	}

	cancel(event: SenderSubscriptionEvent): Observable<SenderEventActionResponse> {
		const params: SenderEventActionRequest = {
			Data: {
				Ids: [event.sender.id],
				Entities: [event],
			},
		};
		const notify = (response: SenderEventActionResponse): void => {
			const message = this.langService.translate('notifications.actions.cancel', { entity: this.entity });
			if (response.Success) {
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<SenderEventActionResponse>('SenderEvents.Cancel', params, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap(() => {
					this.refresh$.next();
				})
			);
	}

	private readonly errorNotifier = (): ErrorNotifierConfig => ({ title: this.title });
}
